import React, { useEffect, useState } from 'react'
import AddEditForm from '../../../sharedComponents/AddEditForm'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageKpiLists from './useManageKpiLists';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '600px',
      maxHeight: '90vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const KpiListSetting = () => {
  const [mode, setMode] = useState('register')
  const [modalTitle, setModalTitle] = useState('Register KPI List')
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [kpiList, setKpiList] = useState({
    id: '',
    group: '',
    topic: '',
    detail: '',
    deductPoint: '',
    isActive: true
  });

  const {
    kpiLists,
    isLoading,
    error,
    addKpiList,
    editKpiList,
    deleteKpiList,
    refreshKpiLists
  } = useManageKpiLists();

  const items = [
    {
      title: 'Group',
      name: 'group',
      type: 'text',
    },
    {
      title: 'Topic',
      name: 'topic',
      type: 'text',
    },
    {
      title: 'Detail',
      name: 'detail',
      type: 'textarea',
    },
    {
      title: 'Deduct Point',
      name: 'deductPoint',
      type: 'number',
      step: '0.5',
    },
    {
      title: 'Is Active',
      name: 'isActive',
      type: 'boolean',
    },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setKpiList({
      ...kpiList,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const openForm = async (isEdit, id) => {
    if (isEdit) {
      const kpiListToEdit = kpiLists.find(k => k.id === id);
      if (kpiListToEdit) {
        setKpiList({
          id: kpiListToEdit.id,
          group: kpiListToEdit.group || '',
          topic: kpiListToEdit.topic || '',
          detail: kpiListToEdit.detail || '',
          deductPoint: kpiListToEdit.deductPoint || '',
          isActive: kpiListToEdit.isActive !== undefined ? kpiListToEdit.isActive : true
        });
        setModalTitle('Update KPI List');
        setMode('edit');
      }
    } else {
      setKpiList({
        id: '',
        group: '',
        detail: '',
        deductPoint: '',
        isActive: true
      });
      setMode('register');
      setModalTitle('Register KPI List');
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  const openConfirm = async (id) => {
    const kpiListToDelete = kpiLists.find(k => k.id === id);
    if (kpiListToDelete) {
      setKpiList(kpiListToDelete);
      setIsConfirmOpen(true);
    }
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
  };

  const handleRegisterKpiList = async () => {
    try {
      const returnResult = await addKpiList(kpiList);
      if (returnResult) {
        toast.success('KPI List registered successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    toast.error(error);
  }, [error]);

  const handleUpdateKpiList = async () => {
    try {
      await editKpiList(kpiList.id, kpiList);
      toast.success('KPI List updated successfully!');
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteKpiList = async (id) => {
    try {
      await deleteKpiList(id);
      toast.success('KPI List deleted successfully!');
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    refreshKpiLists();
  }, []);

  return (
    <div>
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm 
          items={items} 
          name={modalTitle} 
          data={kpiList} 
          mode={mode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleRegister={handleRegisterKpiList}
          handleUpdate={handleUpdateKpiList}
          stateValue={kpiList}
        />
      </Modal>
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          items={items} 
          data={kpiList} 
          closeConfirm={closeConfirm}
          confirmFunction={() => handleDeleteKpiList(kpiList.id)}
        />
      </Modal>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex flex-col">
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-5xl'>KPI List Setting</h2>
            <button 
              onClick={() => openForm(false, null)} 
              className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
            >
              Add New KPI
            </button>
          </div>
          <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th scope="col" className="py-4 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      No
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Group
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Detail
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Deduct Point
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Active
                    </th>
                    <th scope="col" className="relative py-4 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {kpiLists.map((kpiItem, index) => {
                    const { id, group, detail, deductPoint, isActive } = kpiItem;
                    return (
                      <tr key={id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {index + 1}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {group}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {detail}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {deductPoint}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {isActive ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-0 text-right text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <PencilIcon 
                              onClick={() => openForm(true, id)} 
                              className='cursor-pointer flex items-center w-6 h-6 fill-green-600'
                            />
                            <TrashIcon 
                              onClick={() => openConfirm(id)} 
                              className='cursor-pointer flex items-center w-6 h-6 fill-red-600'
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KpiListSetting;

